import { Box, Grid, styled, Switch } from '@mui/material';
import TextFieldLimited from 'Components/Forms/TextFieldLimited';
import { IPlotValues } from 'FeatureModules/Site/schemas/sites.schema';
import { FieldWrapper, InfoTooltip, ThemeTypography } from 'DesignSystem';
import { FastField as Field, useField, useFormikContext } from 'formik';
import { TextField } from 'formik-mui';
import { nanoid } from 'nanoid';
import React, { FC, useEffect, useMemo, useState } from 'react';
import AgroforestryDropdown from '../AgroforestryDropdown';

const StyledCard = styled(Grid)(({ theme }) => ({
  borderRadius: 6,
  background: theme.custom.themeColors.white,
  boxShadow: theme.custom.shadows[4],
  minHeight: 150,
  padding: theme.spacing(2),
  overflowY: 'auto',
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '&.MuiSwitch-root': {
    margin: -8,
  },
}));

const InfoContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: '6px',
  border: `1px solid ${theme.custom.themeColors.primary[40]}`,
  background: theme.custom.themeColors.primary[5],
}));

const SwitchContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  background: theme.custom.themeColors.grayScale[5],
  width: 'fit-content',
  borderRadius: 6,
  height: 32,
  padding: theme.spacing(0, 1),
}));

const PlotBaseForm: FC = () => {
  const { initialValues, values, setFieldValue } = useFormikContext<IPlotValues>();
  const [{ value: featureValue }, , { setValue: setFeatureValue }] =
    useField<IPlotValues['feature']>('feature');
  const [{ value: plotIdValue }, , { setValue: setPlotIdValue }] =
    useField<IPlotValues['plotId']>('plotId');
  const [{ value: isAgroforestryValue }, , { setValue: setIsAgroforestryValue }] =
    useField<IPlotValues['isAgroforestry']>('isAgroforestry');

  const [disablePlotId, setDisablePlotId] = useState(false);

  const disableSizeInput = useMemo(
    () => featureValue?.geometry?.type === 'Polygon',
    [featureValue]
  );

  /** Workaround to fix the state issue of the size input disabling and enabling it correctly */
  useEffect(() => {
    if (!disableSizeInput && initialValues.size !== values.size) {
      setFieldValue('size', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disableSizeInput]);

  /**
   * Automatically assign plot ID for new plots inserted into geojson editor and populate the plot ID field if the field is empty
   * Align plot ID with the feature properties plotId
   */
  useEffect(() => {
    if (!plotIdValue && featureValue?.properties?.plotId) {
      setPlotIdValue(featureValue.properties.plotId);
    } else if (featureValue && !featureValue.properties?.plotId && plotIdValue) {
      setFeatureValue({
        ...featureValue,
        properties: {
          ...featureValue?.properties,
          plotId: plotIdValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureValue]);

  /**
   * If plot ID field is updated, reflect the change in the feature properties plotId
   */
  useEffect(() => {
    if (plotIdValue && featureValue && plotIdValue !== featureValue.properties?.plotId) {
      setFeatureValue({
        ...featureValue,
        properties: {
          ...featureValue.properties,
          plotId: plotIdValue,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plotIdValue]);

  const handleAutoAssignToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setDisablePlotId(true);
      // Generate a unique ID and set it as the Plot ID
      const uniqueId = nanoid(12);
      setPlotIdValue(uniqueId);
      if (featureValue) {
        setFeatureValue({
          ...featureValue,
          properties: {
            ...featureValue.properties,
            plotId: uniqueId,
          },
        });
      }
    } else {
      setDisablePlotId(false);
      // Clear the Plot ID when auto-assign is disabled
      setPlotIdValue('');
    }
  };

  return (
    <StyledCard container rowSpacing={2}>
      <Grid item xs={12} mt={-2}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FieldWrapper label="Plot title" fullWidth>
              <Field
                name="title"
                fullWidth
                placeholder="Enter name"
                component={TextFieldLimited}
                variant="outlined"
                size="small"
                charLimit={50}
              />
            </FieldWrapper>
          </Grid>
          <Grid item xs={12}>
            <FieldWrapper
              label="Plot size (ha)"
              fullWidth
              tooltip={{
                helperText:
                  'The size of the plot in hectares. This will be automatically calculated for polygon plots.',
                variant: 'INFO',
              }}
            >
              <Field
                name="size"
                fullWidth
                placeholder="Enter number"
                disabled={disableSizeInput}
                component={TextField}
                type="number"
                variant="outlined"
                size="small"
              />
            </FieldWrapper>
          </Grid>
          <Grid item xs={7}>
            <FieldWrapper label="Plot ID" required fullWidth>
              <Field
                component={TextFieldLimited}
                placeholder="Enter ID"
                fullWidth
                name="plotId"
                variant="outlined"
                disabled={disablePlotId}
                size="small"
                inputProps={{
                  'data-testid': 'internal-id-input',
                }}
              />
            </FieldWrapper>
          </Grid>
          <Grid item xs={5}>
            <Box width="100%" height="100%" display="flex" alignItems="end">
              <SwitchContainer>
                <ThemeTypography variant="BODY_SMALL_BOLD" autoOverflow maxWidth={100}>
                  Auto-assign number
                </ThemeTypography>
                <StyledSwitch type="checkbox" onChange={handleAutoAssignToggle} />
                <InfoTooltip
                  variant="INFO"
                  text="If you do not use internal plot IDs, you can generate a unique number by enabling this option."
                />
              </SwitchContainer>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <FieldWrapper label="Internal farm ID" fullWidth>
              <Field
                component={TextFieldLimited}
                placeholder="Enter ID"
                fullWidth
                disabled
                name="farmId"
                variant="outlined"
                size="small"
                inputProps={{
                  'data-testid': 'internal-id-input',
                }}
              />
            </FieldWrapper>
          </Grid>
        </Grid>
      </Grid>
      <InfoContainer mt={2}>
        <ThemeTypography variant="BODY_MEDIUM" color="GRAY_100">
          Be careful about editing IDs. This could break links to your internal system or your
          partner’s ID references.
        </ThemeTypography>
      </InfoContainer>
      <Grid item xs={12}>
        <AgroforestryDropdown value={isAgroforestryValue} onChange={setIsAgroforestryValue} />
      </Grid>
    </StyledCard>
  );
};

export default PlotBaseForm;
